(() => {
  'use strict';

  if (window.SJWFromSeed) return;

  const instances = new WeakMap();

  const viewportCategory = () => {
    if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 989px)').matches) return 'tablet';
    return 'desktop';
  };

  const pushEvent = (root, eventName, detail = {}) => {
    if (!eventName) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      page_handle: root.dataset.pageHandle || 'grown-not-mined',
      viewport_category: viewportCategory(),
      ...detail,
    });
  };

  class FromSeedPage {
    constructor(root) {
      this.root = root;
      this.cleanups = [];
      this.viewedStages = new Set();
      this.init();
    }

    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    init() {
      this.initAnalytics();
      this.initStageNavigation();
      this.initFaqs();
      this.initComparison();
      this.initVideo();
    }

    initAnalytics() {
      this.listen(this.root, 'click', (event) => {
        const link = event.target.closest('[data-fs-event]');
        if (!link || !this.root.contains(link)) return;

        const eventName = link.dataset.fsEvent;
        const destination = link.getAttribute('href') || '';
        pushEvent(this.root, eventName, {
          cta_label: link.dataset.fsLabel || link.textContent.trim(),
          destination,
        });
      });
    }

    initStageNavigation() {
      const stages = [...this.root.querySelectorAll('[data-fs-stage]')];
      const links = [...this.root.querySelectorAll('[data-fs-stage-link]')];
      if (!stages.length) return;

      const setCurrent = (stageNumber) => {
        links.forEach((link) => {
          if (link.dataset.fsStageLink === String(stageNumber)) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      };

      setCurrent(1);

      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

          if (!visible.length) return;
          const stageNumber = Number(visible[0].target.dataset.fsStage);
          setCurrent(stageNumber);

          if (!this.viewedStages.has(stageNumber)) {
            this.viewedStages.add(stageNumber);
            pushEvent(this.root, 'from_seed_stage_view', { stage_number: stageNumber });
          }
        },
        { rootMargin: '-18% 0px -42% 0px', threshold: [0.2, 0.45, 0.7] }
      );

      stages.forEach((stage) => observer.observe(stage));
      this.cleanups.push(() => observer.disconnect());
    }

    initFaqs() {
      this.root.querySelectorAll('[data-fs-faq]').forEach((details) => {
        const onToggle = () => {
          if (!details.open) return;
          const question = details.querySelector('summary')?.textContent.trim() || '';
          pushEvent(this.root, 'from_seed_faq_open', { faq_question: question });
        };
        this.listen(details, 'toggle', onToggle);
      });
    }

    initComparison() {
      this.root.querySelectorAll('[data-fs-compare]').forEach((comparison) => {
        const range = comparison.querySelector('[data-fs-compare-range]');
        if (!range) return;
        const update = () => comparison.style.setProperty('--fs-compare-position', `${range.value}%`);
        update();
        this.listen(range, 'input', update);
        this.listen(range, 'change', update);
      });
    }

    initVideo() {
      const videos = [...this.root.querySelectorAll('[data-fs-video] video')];
      if (!videos.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const toggle = this.root.querySelector('[data-fs-video-toggle]');
      const label = toggle?.querySelector('[data-fs-video-label]');
      let userPaused = reducedMotion.matches;
      let heroInView = false;
      const stopAll = () => videos.forEach((video) => video.pause());

      const updateControl = () => {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        if (label) label.textContent = userPaused ? 'Play motion' : 'Pause motion';
      };

      const playAll = () => {
        if (userPaused || !heroInView) return;
        videos.forEach((video) => {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              userPaused = true;
              updateControl();
            });
          }
        });
      };

      updateControl();
      stopAll();

      if (toggle) {
        this.listen(toggle, 'click', () => {
          userPaused = !userPaused;
          updateControl();
          if (userPaused) stopAll();
          else playAll();
        });
      }

      const onMotionChange = (event) => {
        if (!event.matches) return;
        userPaused = true;
        stopAll();
        updateControl();
      };
      this.listen(reducedMotion, 'change', onMotionChange);

      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            heroInView = entry.isIntersecting;
            if (entry.isIntersecting) {
              playAll();
            } else {
              stopAll();
            }
          });
        },
        { threshold: 0.2 }
      );

      const videoContainer = this.root.querySelector('[data-fs-video]');
      if (videoContainer) observer.observe(videoContainer);
      this.cleanups.push(() => {
        observer.disconnect();
        stopAll();
      });
    }

    destroy() {
      this.cleanups.splice(0).forEach((cleanup) => cleanup());
      instances.delete(this.root);
    }
  }

  const mount = (scope = document) => {
    const roots = scope.matches?.('[data-from-seed]')
      ? [scope]
      : [...scope.querySelectorAll?.('[data-from-seed]') || []];

    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new FromSeedPage(root));
    });
  };

  const destroy = (scope) => {
    const roots = scope.matches?.('[data-from-seed]')
      ? [scope]
      : [...scope.querySelectorAll?.('[data-from-seed]') || []];

    roots.forEach((root) => instances.get(root)?.destroy());
  };

  window.SJWFromSeed = { mount, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  } else {
    mount(document);
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
